import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { RouteHelpers } from "../../../../../root/routes";
import { Text } from "../../../../../../common/components/input/Text";
import { Form } from "react-final-form";
import { SUBMIT_FIELD_NAMES } from "../../../DataRequestSubjectCard";
import { Api } from "../../../../../../common/service/Api";
import { useMutation } from "@tanstack/react-query";
import { Typography } from "@mui/material";
import { DataRequestDetail } from "../../../../../../common/models";
import { DataRequestCustomFieldDisplay } from "../../DataRequestCustomFieldDisplay";

const checkIfCompleted = (request: DataRequestDetail | AppealFormValues) => {
  if (request?.appealDecisionIdValidated == false) {
    return true;
  }
  if (request?.appealResolutionDescription?.trim().length > 0) {
    return true;
  }
  return false;
};

type AppealFormValues = {
  appealDecisionIdValidated?: boolean;
  appealResolutionDescription: string;
};

export const ResolveAppeal = () => {
  const [org] = usePrimaryOrganization();

  const { request, setStepCompleted } = useDataRequestProcessingContext();
  const appealedRequestUrl = RouteHelpers.requests.detail(request.appealDecisionId);

  const [timer, setTimer] = useState<any | null>(null);

  const updateAppealMutation = useMutation({
    mutationFn: async (values: AppealFormValues) => {
      const existingValues = Object.fromEntries(
        Object.entries(request).filter(([k]) => SUBMIT_FIELD_NAMES.includes(k)),
      );
      const updatedValues = Object.assign({}, existingValues, {
        appealResolutionDescription: values.appealResolutionDescription || "",
      }) as any;

      setStepCompleted(
        checkIfCompleted({
          ...values,
          appealDecisionIdValidated: request.appealDecisionIdValidated,
        }),
      );
      return Api.dataRequest.updateRequest(org.id, request.id, updatedValues);
    },
  });

  useEffect(() => {
    setStepCompleted(checkIfCompleted(request));
  }, [request, setStepCompleted]);

  return (
    <>
      {request.appealDecisionIdValidated ? (
        <>
          <DataRequestCustomFieldDisplay org={org} request={request} classList="mb-std" />

          <div className="text-t2 mb-std">
            Use the text box below to descibe the appeal for the Privacy Request ID:{" "}
            <Link className="text-weight-regular" to={appealedRequestUrl} target="_blank">
              {request.appealDecisionId}
            </Link>{" "}
            and how your company resolved it.
          </div>
          <Typography variant="body2" color="textSecondary" className="ml-xs">
            This information will be included in the email notification to the consumer
          </Typography>
          <Form
            onSubmit={(values: { appealResolutionDescription: string }) =>
              updateAppealMutation.mutate(values)
            }
            onBlur={(values: { appealResolutionDescription: string }) =>
              updateAppealMutation.mutate(values)
            }
            initialValues={{
              appealResolutionDescription: request.appealResolutionDescription || "",
            }}
            render={({ handleSubmit }) => {
              const debouncedSubmit = (event: React.ChangeEvent<HTMLFormElement>) => {
                if (timer) {
                  clearTimeout(timer);
                }

                const newTimer = setTimeout(() => {
                  handleSubmit(event);
                }, 750);

                setTimer(newTimer);
              };

              return (
                <form onChange={debouncedSubmit} onBlur={debouncedSubmit}>
                  <Text
                    fullWidth
                    field="appealResolutionDescription"
                    multiline
                    rows={4}
                    variant="outlined"
                    required={true}
                  />
                  {updateAppealMutation.isError && (
                    <div className="text-red-600 mt-2">
                      Failed to update appeal description. Please try again.
                    </div>
                  )}
                </form>
              );
            }}
          />
        </>
      ) : (
        <div className="text-tw mb-std">
          Step skipped since a valid Privacy Request ID: {request.appealDecisionId} could not be
          located.
        </div>
      )}
    </>
  );
};
